/**
 * Modern Mapbox Geocoding Service
 * Uses the latest Search Box API v1 for better autocomplete and performance
 */

import { MAPBOX_ACCESS_TOKEN } from './mapbox';

// Generate a unique session token for billing optimization
const generateSessionToken = (): string => {
  return `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

// Cache for session tokens (expires after 5 minutes)
const sessionCache = new Map<string, { token: string; expires: number }>();

export interface SearchSuggestion {
  mapbox_id: string;
  name: string;
  full_address?: string;
  place_formatted?: string;
  context?: {
    country?: { name: string; country_code?: string };
    region?: { name: string; region_code?: string };
    postcode?: { name: string };
    district?: { name: string };
    place?: { name: string };
    locality?: { name: string };
    neighborhood?: { name: string };
    street?: { name: string };
    address?: { name: string; address_number?: string };
  };
  coordinates?: {
    longitude: number;
    latitude: number;
  };
  feature_type: string;
  properties?: Record<string, any>;
}

export interface RetrieveResult extends SearchSuggestion {
  geometry: {
    coordinates: [number, number];
    type: string;
  };
}

export class MapboxGeocodingService {
  private baseUrl = 'https://api.mapbox.com';
  private accessToken: string;
  private currentSessionToken: string | null = null;
  private abortController: AbortController | null = null;

  constructor(accessToken?: string) {
    this.accessToken = accessToken || MAPBOX_ACCESS_TOKEN;
    if (!this.accessToken || this.accessToken === 'your_mapbox_public_token') {
      console.warn('Mapbox access token not properly configured');
    }
  }

  /**
   * Get or create a session token for the current search session
   */
  private getSessionToken(sessionId: string = 'default'): string {
    const cached = sessionCache.get(sessionId);
    const now = Date.now();

    if (cached && cached.expires > now) {
      return cached.token;
    }

    const token = generateSessionToken();
    sessionCache.set(sessionId, {
      token,
      expires: now + 5 * 60 * 1000 // 5 minutes
    });

    return token;
  }

  /**
   * Search for address suggestions using the Search Box API /suggest endpoint
   * This is optimized for interactive autocomplete scenarios
   */
  async suggest(
    query: string,
    options: {
      sessionId?: string;
      limit?: number;
      proximity?: string; // 'ip' or 'longitude,latitude'
      bbox?: string; // 'minLon,minLat,maxLon,maxLat'
      country?: string; // ISO 3166 codes
      types?: string[]; // Filter by feature types
      language?: string;
    } = {}
  ): Promise<SearchSuggestion[]> {
    if (!query || query.trim().length < 2) {
      return [];
    }

    // Cancel previous request if still pending
    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();
    const sessionToken = this.getSessionToken(options.sessionId);

    try {
      const params = new URLSearchParams({
        q: query,
        access_token: this.accessToken,
        session_token: sessionToken,
        language: options.language || 'en',
        limit: String(options.limit || 5),
      });

      // Add optional parameters
      if (options.country) {
        params.append('country', options.country);
      }
      if (options.proximity) {
        params.append('proximity', options.proximity);
      }
      if (options.bbox) {
        params.append('bbox', options.bbox);
      }
      if (options.types && options.types.length > 0) {
        params.append('types', options.types.join(','));
      }

      // Use Search Box API /suggest endpoint for interactive autocomplete
      const url = `${this.baseUrl}/search/searchbox/v1/suggest?${params}`;

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          'Accept': 'application/json',
          'Accept-Encoding': 'gzip, deflate',
        }
      });

      if (!response.ok) {
        if (response.status === 406) {
          console.error('Mapbox API returned 406: Check API token and request format');
          throw new Error('Invalid request format or authentication issue');
        }
        throw new Error(`Geocoding failed: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      // Transform the response from suggestions array to our format
      return (data.suggestions || []).map((suggestion: any) => ({
        mapbox_id: suggestion.mapbox_id,
        name: suggestion.name,
        full_address: suggestion.full_address || suggestion.place_formatted,
        place_formatted: suggestion.place_formatted,
        context: suggestion.context,
        coordinates: suggestion.coordinates,
        feature_type: suggestion.feature_type,
        properties: suggestion,
      }));

    } catch (error: any) {
      if (error.name === 'AbortError') {
        // Request was cancelled, this is expected behavior
        return [];
      }
      console.error('Geocoding error:', error);
      throw error;
    } finally {
      this.abortController = null;
    }
  }

  /**
   * Retrieve detailed information about a specific suggestion
   * Call this when user selects an item from suggestions
   * This completes the suggest -> retrieve flow for the Search Box API
   */
  async retrieve(
    mapboxId: string,
    options: {
      sessionToken?: string;
    } = {}
  ): Promise<RetrieveResult | null> {
    try {
      const params = new URLSearchParams({
        access_token: this.accessToken,
        session_token: options.sessionToken || this.getSessionToken(),
      });

      const url = `${this.baseUrl}/search/searchbox/v1/retrieve/${mapboxId}?${params}`;

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to retrieve details: ${response.status}`);
      }

      const data = await response.json();

      if (!data.features || data.features.length === 0) {
        return null;
      }

      const feature = data.features[0];
      const props = feature.properties;

      return {
        mapbox_id: mapboxId,
        name: props.name,
        full_address: props.full_address,
        place_formatted: props.place_formatted,
        context: props.context,
        coordinates: {
          longitude: feature.geometry.coordinates[0],
          latitude: feature.geometry.coordinates[1],
        },
        geometry: feature.geometry,
        feature_type: props.feature_type,
        properties: props,
      };

    } catch (error) {
      console.error('Failed to retrieve location details:', error);
      throw error;
    }
  }

  /**
   * Legacy forward geocoding using v6 API
   * Fallback for when Search Box API is not suitable
   */
  async forwardGeocode(
    query: string,
    options: {
      limit?: number;
      bbox?: string;
      proximity?: string;
      country?: string;
      types?: string[];
    } = {}
  ): Promise<any> {
    const params = new URLSearchParams({
      q: query,
      access_token: this.accessToken,
      limit: String(options.limit || 5),
    });

    if (options.country) params.append('country', options.country);
    if (options.proximity) params.append('proximity', options.proximity);
    if (options.bbox) params.append('bbox', options.bbox);
    if (options.types) params.append('types', options.types.join(','));

    const url = `${this.baseUrl}/search/geocode/v6/forward?${params}`;

    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Geocoding failed: ${response.status}`);
    }

    return response.json();
  }

  /**
   * Cancel any pending requests
   */
  cancelPendingRequests() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }
}

// Export a default instance
export const geocodingService = new MapboxGeocodingService();